import { useState, type ReactNode } from "react";
import { cn } from "./cn";

export interface EditPaneTab {
  id: string;
  label: string;
  content: ReactNode;
  /** Hides the tab entirely — used where a tab only applies to one entity type. */
  hidden?: boolean;
  /**
   * A count or status shown beside the label. A plain number or string renders
   * as `(n)`; pass an element (a `<Badge>`) to render it as-is.
   */
  badge?: ReactNode;
  /**
   * Drops the pane's padding so the content runs edge to edge.
   *
   * For tabs whose content is a grid and nothing else. A grid brings its own
   * edges (toolbar, filter row, header, row borders, its own scroll container),
   * so padding around it reads as a second frame and costs a column of width
   * on wide tables. It also keeps a grid in a tab looking like the same
   * component as a grid on a list page, which is always flush. Forms and mixed
   * content keep the padding: inputs need room against the panel edge.
   */
  flush?: boolean;
}

export interface EditPaneProps {
  tabs: EditPaneTab[];
  /** Rendered above the tabs — identity, status, key figures. */
  header?: ReactNode;
  /** Save / Cancel / Close, pinned to the bottom. */
  footer?: ReactNode;
  initialTab?: string;
  /**
   * Keeps the open tab in the URL under this query parameter (`?tab=bom`).
   *
   * Without it the tab lives only in memory: leave the page and come back with
   * the browser's Back, and you land on the first tab instead of the one you
   * were on; and no link can open a record on a given tab. With it the tab is
   * read from the URL when the pane mounts and written back — replacing the
   * history entry, so switching tabs does not pile up Back steps. Set it on a
   * page's main pane only; two panes on one page would share the parameter.
   */
  urlParam?: string;
}

function readUrlTab(param: string | undefined): string | null {
  if (!param || typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get(param);
  } catch {
    return null;
  }
}

function writeUrlTab(param: string, tab: string): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(param) === tab) return;
    url.searchParams.set(param, tab);
    // Keep the router's own history state (its entry key and index): Back and
    // Forward depend on it.
    window.history.replaceState(window.history.state, "", url);
  } catch {
    // The URL is a convenience; the pane works without it.
  }
}

/**
 * A tabbed detail view — the `ATabFrame` equivalent.
 *
 * Tabs scroll horizontally rather than wrapping, because entities like a
 * product carry more tabs than fit any window, and a wrapping tab bar that
 * grows to three rows pushes the content off-screen.
 */
export function EditPane({ tabs, header, footer, initialTab, urlParam }: EditPaneProps) {
  const visible = tabs.filter((tab) => !tab.hidden);
  const [active, setActiveState] = useState(() => readUrlTab(urlParam) ?? initialTab ?? visible[0]?.id ?? "");
  const setActive = (tab: string) => {
    setActiveState(tab);
    if (urlParam) writeUrlTab(urlParam, tab);
  };

  // A tab can disappear when the entity changes shape (a bundle becoming a
  // single item); fall back rather than render nothing.
  const current = visible.find((tab) => tab.id === active) ?? visible[0];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {header && <div className="border-b border-line bg-surface px-4 py-3 lg:px-6">{header}</div>}

      <div className="flex gap-1 overflow-x-auto border-b border-line bg-surface px-2">
        {visible.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === current?.id}
            onClick={() => setActive(tab.id)}
            className={cn(
              "flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-[13px]",
              tab.id === current?.id
                ? "border-accent font-medium text-accent"
                : "border-transparent text-ink-muted hover:text-ink",
            )}
          >
            {tab.label}
            {renderBadge(tab.badge)}
          </button>
        ))}
      </div>

      {/*
        A flush tab is a flex column so the grid can take the full height and
        scroll inside itself (that is how DataTable is built: min-h-0 flex-1
        with its own scroll container, virtualized against it). Padded tabs
        stay block-level, which is the layout their forms were written for.
      */}
      <div
        className={cn(
          "min-h-0 flex-1 overflow-auto",
          current?.flush ? "flex flex-col" : "p-4 lg:p-6",
        )}
      >
        {current?.content}
      </div>

      {footer && (
        <div className="flex items-center justify-end gap-2 border-t border-line bg-surface px-4 py-3 lg:px-6">
          {footer}
        </div>
      )}
    </div>
  );
}

/**
 * A count sits in its own element for two reasons: parentheses read better than
 * a bare number against a word, and adjacent text nodes collapse into a single
 * anonymous flex item, so the tab bar's `gap` never applied and the label ran
 * straight into the number ("Quantity3").
 */
function renderBadge(badge: ReactNode): ReactNode {
  if (badge === null || badge === undefined) return null;
  if (typeof badge === "number" || typeof badge === "string") {
    return <span>({badge})</span>;
  }
  return badge;
}

/** Placeholder for a tab whose screen hasn't been ported yet. */
export function TabNotPorted({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-line p-8 text-center">
      <p className="text-[13px] text-ink-muted">{message}</p>
    </div>
  );
}
